import React, { useEffect } from 'react';
import { Badge, Button, Row } from 'antd';
import { NavigateOptions, useLocation } from 'react-router-dom';
import {
  ExportOutlined,
  TeamOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { EChannelProvider } from '@/services/solgari-api/enums/channel-provider.enum';
import { EHistoryProvider } from '@/shared/interfaces/history-filter.interface';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import calendarIcon from '@/public/icons/side-bar/calendar.svg';
import phoneBookIcon from '@/public/icons/side-bar/phone-book.svg';
import dialPadIcon from '@/public/icons/side-bar/dial-pad.svg';
import inboxIcon from '@/public/icons/side-bar/inbox.svg';
import voicemailIcon from '@/public/icons/side-bar/voicemail.svg';
import dialerIcon from '@/public/icons/side-bar/dialer.svg';
import { DefaultList } from '@/public/icons/colorful/DefaultList';
import { MsgIcon } from '@/public/icons/colorful/MsgIcon';
import { FBFilterIcon } from '@/public/icons/colorful/FBFilterIcon';
import { WatsupIcon } from '@/public/icons/colorful/WatsupIcon';
import { OmniCall } from '@/public/icons/filters/OmniCall';
import emailIcon from '@/public/icons/email.svg';
import lineIcon from '@/public/icons/line.svg';
import liveChat from '@/public/icons/live-chat.svg';
import { useStore } from '@/stores';
import { EModeValue } from '@/services/dynamics/enums/mode.enum';
import { CALL_PAGE_TYPE, CALL_PAGE_STATUS } from '@/shared/constants/callPage';
import { SipJs } from '@/services/sip-js.service';
import { ForceReload } from '@/components/ForceReload/ForceReload';
import './SideBar.scss';

export type NavigateType =
  | 'activity-history'
  | 'dial-pad'
  | 'logout'
  | 'entities'
  | 'login'
  | 'inbox'
  | 'voicemail-list'
  | 'dialer'
  | 'settings'
  | 'force-reload'
  | string;

export interface SideBarProps {
  onNavigate: (navigateType: NavigateType, options?: NavigateOptions) => void;

  isLogged: boolean;
  voicemailLength: string;
}

export const SideBar = observer(
  ({ onNavigate, isLogged, voicemailLength }: SideBarProps) => {
    const isDev = !!localStorage.getItem('dev');

    const location = useLocation();
    const {
      chatsStore: {
        unstoppedCount,
        setInboxFilter,
        inboxFilter,
        historyFilter,
        setHistoryFilter,
      },
      callStore: {
        widgetMode,
        callPage: { page, status },
        setCallPage,
      },
    } = useStore();
    const { chatExceptions } = inboxFilter;
    const { exceptions } = historyFilter;

    const handleApplyInboxFilter = (filter: EChannelProvider) => {
      if (!chatExceptions.includes(filter)) {
        setInboxFilter({ chatExceptions: [...chatExceptions, filter] });
        return;
      }

      const filteredChats = chatExceptions.filter((c) => c !== filter);
      setInboxFilter({ chatExceptions: [...filteredChats] });
    };

    const clearInboxMessengerFilters = () => {
      setInboxFilter({ chatExceptions: [] });
    };

    const handleApplyHistoryFilter = (filter: EHistoryProvider) => {
      if (!exceptions.includes(filter)) {
        setHistoryFilter({ exceptions: [...exceptions, filter] });
        return;
      }

      const filteredChats = exceptions.filter((c) => c !== filter);
      setHistoryFilter({ exceptions: [...filteredChats] });
    };

    const clearHistoryMessengerFilters = () => {
      setHistoryFilter({ exceptions: [] });
    };

    const shouldShowInbox = (): boolean =>
      widgetMode === EModeValue.DOCK && location.pathname.includes('/inbox');

    const shouldShowHistory = (): boolean =>
      widgetMode === EModeValue.DOCK &&
      (location.pathname.includes('/activity-history') ||
        location.pathname.includes('/call-record'));

    const handleWarning = () => {
      onNavigate('force-reload');
    };

    // Open chat with filter during active call by filter choose (minimize active call page)
    useEffect(() => {
      if (page === CALL_PAGE_TYPE.FULL && status === CALL_PAGE_STATUS.ACTIVE) {
        setCallPage({ page: CALL_PAGE_TYPE.MINIMIZED });
      }
      if (shouldShowInbox()) {
        onNavigate('inbox');
      }
    }, [inboxFilter]);

    // Open filter tabs for call record page with ability to navigate to activity history by clicking any filter icon
    useEffect(() => {
      if (shouldShowHistory()) {
        onNavigate('activity-history');
      }
    }, [historyFilter]);

    return (
      <div className="side-bar">
        {isLogged && (
          <>
            <div className="title" title="Dial Pad">
              <Button
                type="text"
                data-testid="navigate-dial-pad"
                onClick={() => onNavigate('dial-pad')}
                disabled={!!SipJs.conference?.conferenceId}
                icon={<MessageIcon svgUrl={dialPadIcon} />}
              ></Button>
            </div>
            <div
              className="title"
              title="Inbox"
              data-testid="navigate-inbox"
              onClick={() => onNavigate('inbox')}
            >
              <Badge
                size="small"
                count={unstoppedCount || ''}
                className="badge-inbox"
              >
                <Button
                  type="text"
                  icon={<MessageIcon svgUrl={inboxIcon} />}
                ></Button>
              </Badge>
            </div>
            {shouldShowInbox() ? (
              <div className="filterswrap">
                <Row>
                  <button
                    data-testid="clear-inbox-filters"
                    className={`filter-button ${chatExceptions.length === 0 ? '-active' : ''}`}
                    onClick={clearInboxMessengerFilters}
                  >
                    <DefaultList />
                  </button>
                </Row>

                <Row>
                  <button
                    data-testid="apply-inbox-filters-sms"
                    className={`filter-button ${chatExceptions.includes(EChannelProvider.SMS) ? '-active' : ''}`}
                    onClick={() => handleApplyInboxFilter(EChannelProvider.SMS)}
                  >
                    <MsgIcon />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-inbox-filters-facebook"
                    className={`filter-button ${chatExceptions.includes(EChannelProvider.FACEBOOK) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyInboxFilter(EChannelProvider.FACEBOOK)
                    }
                  >
                    <FBFilterIcon />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-inbox-filters-whatsapp"
                    className={`filter-button ${chatExceptions.includes(EChannelProvider.WHATSAPP) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyInboxFilter(EChannelProvider.WHATSAPP)
                    }
                  >
                    <WatsupIcon />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-inbox-filters-outlook"
                    className={`filter-button ${chatExceptions.includes(EChannelProvider.OUTLOOK) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyInboxFilter(EChannelProvider.OUTLOOK)
                    }
                  >
                    <MessageIcon className="email-icon" svgUrl={emailIcon} />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-inbox-filters-line"
                    className={`filter-button ${chatExceptions.includes(EChannelProvider.LINE) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyInboxFilter(EChannelProvider.LINE)
                    }
                  >
                    <MessageIcon className="email-icon" svgUrl={lineIcon} />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-inbox-filters-live-chat"
                    className={`filter-button ${chatExceptions.includes(EChannelProvider.LIVE_CHAT) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyInboxFilter(EChannelProvider.LIVE_CHAT)
                    }
                  >
                    <MessageIcon svgUrl={liveChat} />
                  </button>
                </Row>
              </div>
            ) : (
              <></>
            )}
            <Badge count={voicemailLength} color="magenta">
              <div className="title" title="Voicemails">
                <Button
                  type="text"
                  data-testid="navigate-voicemail-list"
                  onClick={() => onNavigate('voicemail-list')}
                  icon={<MessageIcon svgUrl={voicemailIcon} />}
                ></Button>
              </div>
            </Badge>

            <div className="title" title="History">
              <Button
                type="text"
                data-testid="navigate-activity-history"
                onClick={() => onNavigate('activity-history')}
                icon={<MessageIcon svgUrl={calendarIcon} />}
              ></Button>
            </div>
            {shouldShowHistory() ? (
              <div className="filterswrap">
                <Row>
                  <button
                    data-testid="clear-history-filters"
                    className={`filter-button ${exceptions.length === 0 ? '-active' : ''}`}
                    onClick={clearHistoryMessengerFilters}
                  >
                    <DefaultList />
                  </button>
                </Row>

                <Row>
                  <button
                    data-testid="apply-history-filters-sms"
                    className={`filter-button ${exceptions.includes(EHistoryProvider.SMS) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.SMS)
                    }
                  >
                    <MsgIcon />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-history-filters-facebook"
                    className={`filter-button ${exceptions.includes(EHistoryProvider.FACEBOOK) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.FACEBOOK)
                    }
                  >
                    <FBFilterIcon />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-history-filters-whatsapp"
                    className={`filter-button  ${exceptions.includes(EHistoryProvider.WHATSAPP) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.WHATSAPP)
                    }
                  >
                    <WatsupIcon />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-history-filters-phone"
                    className={`filter-button ${exceptions.includes(EHistoryProvider.PHONE) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.PHONE)
                    }
                  >
                    <OmniCall />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-history-filters-outlook"
                    className={`filter-button ${exceptions.includes(EHistoryProvider.OUTLOOK) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.OUTLOOK)
                    }
                  >
                    <MessageIcon className="email-icon" svgUrl={emailIcon} />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-history-filters-line"
                    className={`filter-button ${exceptions.includes(EHistoryProvider.LINE) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.LINE)
                    }
                  >
                    <MessageIcon className="email-icon" svgUrl={lineIcon} />
                  </button>
                </Row>
                <Row>
                  <button
                    data-testid="apply-history-filters-live-chat"
                    className={`filter-button ${exceptions.includes(EHistoryProvider.LIVE_CHAT) ? '-active' : ''}`}
                    onClick={() =>
                      handleApplyHistoryFilter(EHistoryProvider.LIVE_CHAT)
                    }
                  >
                    <MessageIcon svgUrl={liveChat} />
                  </button>
                </Row>
              </div>
            ) : (
              <></>
            )}
            <div className="title" title="CRM Records">
              <Button
                type="text"
                data-testid="navigate-entities"
                onClick={() => onNavigate('entities')}
                icon={<TeamOutlined />}
              ></Button>
            </div>
            <div className="title" title="Dialer">
              <Button
                type="text"
                data-testid="navigate-dialer"
                onClick={() => onNavigate('dialer')}
                icon={
                  <MessageIcon className="dialer-icon" svgUrl={dialerIcon} />
                }
              ></Button>
            </div>
            <div className="title" title="PhoneBook">
              <Button
                type="text"
                data-testid="navigate-phonebook"
                onClick={() => onNavigate('phonebook')}
                icon={
                  <MessageIcon className="dialer-icon" svgUrl={phoneBookIcon} />
                }
              ></Button>
            </div>
            <div className="title settings-bar" title="Settings">
              <Button
                type="text"
                data-testid="navigate-settings"
                onClick={() => onNavigate('settings')}
                icon={<SettingOutlined />}
              ></Button>
            </div>
            {isDev && (
              <Button
                type="text"
                onClick={() => onNavigate('logout')}
                icon={<ExportOutlined />}
              ></Button>
            )}
            <div className="title force-reload" title="Force Reload">
              <ForceReload sendWarning={handleWarning} />
            </div>
          </>
        )}
      </div>
    );
  },
);
